import { useEffect, useState } from "react"
import { useAppSelector } from "./use-store"
import { selectStaffAiDrawerOpen } from "../../redux/slices/app"
import { getStaffTopicsPeek } from "../../api/staff-agent"
import { trackAssistantEvent } from "../utils/assistant-analytics"

/**
 * Background new-topics signal for the assistant entry point. On app load
 * (delayed so page-critical requests go first) it peeks GET
 * /api/briefing/topics; when there are unexplored topics the Sparkles
 * button badges to tempt a look. Opening the assistant clears the badge —
 * the greeting's briefing fetch acknowledges the topics server-side, so it
 * stays cleared until something genuinely new appears.
 */
const INITIAL_DELAY_MS = 4_000
const REFRESH_INTERVAL_MS = 10 * 60 * 1000

export function useAssistantTopicsBadge(enabled: boolean): {
  hasNew: boolean
  topics: string[]
} {
  const drawerOpen = useAppSelector(selectStaffAiDrawerOpen)
  const [peek, setPeek] = useState<{ hasNew: boolean; topics: string[] }>({
    hasNew: false,
    topics: []
  })

  useEffect(() => {
    if (!enabled) return
    let cancelled = false

    const check = async () => {
      try {
        const result = await getStaffTopicsPeek()
        if (cancelled) return
        setPeek(prev => {
          if (result.hasNew && !prev.hasNew) {
            trackAssistantEvent("topics_badge_shown", {
              count: result.count
            })
          }
          return { hasNew: result.hasNew, topics: result.topics }
        })
      } catch {
        // Badge is a convenience — stay silent and unbadged on failure.
      }
    }

    const initial = window.setTimeout(check, INITIAL_DELAY_MS)
    const interval = window.setInterval(check, REFRESH_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearTimeout(initial)
      window.clearInterval(interval)
    }
  }, [enabled])

  // Opening the assistant reads the briefing (server-side acknowledgement);
  // clear immediately so the nudge never outlives the look.
  useEffect(() => {
    if (drawerOpen) setPeek({ hasNew: false, topics: [] })
  }, [drawerOpen])

  return peek
}
